import { Box, Typography } from '@mui/material';
import { DateRange, DateRangePicker, MultiInputDateRangeField } from '@mui/x-date-pickers-pro';
import { useSearchkit } from '@searchkit/client';
import { formatISO, parseISO } from 'date-fns';
import React, { useEffect } from 'react';
import { V } from '../../../common/Layout';
import { adjustDateRangeToSuppressLocalTZ, toDateTimeInUTC } from '../../../common/utils/dateTime';

type DateRangeFacetProps = {
    facet: { identifier: string; label: string };
    loading?: boolean;
    isLocal?: boolean;
    autoApply?: boolean;
    onFiltersChanged?: () => void;
};

export const DateRangeFacet = ({ facet, loading, isLocal, autoApply, onFiltersChanged }: DateRangeFacetProps) => {
    const api = useSearchkit();
    const [value, setValue] = React.useState<DateRange<Date>>([null, null]);

    const getSelectedOption = () => {
        const selectedOptions = api.getFiltersByIdentifier(facet.identifier);
        return selectedOptions && selectedOptions[0];
    };

    const onChange = (value) => {
        setValue(value);
        if (value[0] != null && value[1] != null) {
            const selectedOption = getSelectedOption();
            if (selectedOption) {
                api.removeFilter(selectedOption);
            }
            let dateMin: string;
            let dateMax: string;
            if (isLocal) {
                // getting the date in Local TimeZone
                dateMin = formatISO(value[0]);
                dateMax = formatISO(value[1]);
            } else {
                // getting the date in UTC TimeZone
                dateMin = toDateTimeInUTC(value[0]);
                dateMax = toDateTimeInUTC(value[1]);
            }
            const pageSize = api.getSearchState().page.size;
            api.addFilter({
                identifier: facet.identifier,
                dateMin,
                dateMax
            });
            api.setPage({
                from: 0,
                size: pageSize
            });
            onFiltersChanged?.();
            if (autoApply) {
                api.search();
            }
        }
    };

    useEffect(() => {
        const selectedOption = getSelectedOption();
        if (selectedOption) {
            setValue(
                adjustDateRangeToSuppressLocalTZ([parseISO(selectedOption.dateMin), parseISO(selectedOption.dateMax)])
            );
        } else {
            setValue([null, null]);
        }
    }, [api.searchState.filters, facet.identifier]);

    return (
        <V>
            <Typography sx={{ fontSize: 'small' }}>{facet.label}</Typography>
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                <DateRangePicker
                    value={value}
                    onChange={onChange}
                    slots={{ field: MultiInputDateRangeField }}
                    slotProps={{
                        field: {
                            sx: {
                                display: 'flex',
                                gap: 0.5,
                                width: '100%',
                                '& .MuiTextField-root': {
                                    flex: 1,
                                    minWidth: 0
                                }
                            }
                        },
                        textField: {
                            size: 'small',
                            variant: 'standard'
                        }
                    }}
                />
            </Box>
        </V>
    );
};

DateRangeFacet.DISPLAY = 'DateRangeFacet';
